import { useEffect, useMemo, useRef } from 'react';
import closeIcon from '../../assets/close.svg';
import { Divider } from '../elements/Divider';
import CustomInput from '../elements/CustomInput';
import SelectInput from '../elements/SelectInput';
import {
  convertLabelToSlug,
  convertProductClassesToSelectOptions,
} from '../../helper/enum-parser';
import { ProductClassesData, TaxData } from '../../types/location';
import { z as zod } from 'zod';
import { FieldValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, ButtonVariant } from '../elements/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiEndpoint } from '../../constants/endpoints';
import api from '../../lib/api';
import Loader from '../elements/Loader';
import { queryClient } from '../../query-client';
import { ToastHelper, ToastMessageType, ToastType } from '../../helper/toast';
import { ApiErrorResponse } from '../../types/api';
import { parseApiResponseError } from '../../helper/parseApiResponseError';

interface EditTaxRateModalProps {
  handleOpenEditTaxRateModal: () => void;
  taxData: TaxData;
  partnerId: string;
}

const Schema = zod.object({
  name: zod.string().max(50, "Tax name can't contain more than 50 characters"),
  category: zod.string(),
});

type FormSchemaType = zod.infer<typeof Schema>;

const EditTaxRateModal = ({
  handleOpenEditTaxRateModal,
  taxData,
  partnerId,
}: EditTaxRateModalProps) => {
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const {
    data: productClassesDataResponse,
    isLoading: isProductClassesLoading,
  } = useQuery<ProductClassesData, ApiEndpoint>({
    queryKey: ['get_product_classes'],
    queryFn: () =>
      api.fetch(`get_product_classes`, {}) as unknown as ProductClassesData,
    retry: 0,
  });

  const initialFormValues = useMemo(
    () =>
      ({
        name: taxData.tax_rate_name,
        category: taxData?.product_class_slug
          ? taxData?.product_class_slug
          : convertLabelToSlug(taxData?.product_class_name),
      } as FieldValues),
    [taxData],
  );

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<FormSchemaType>({
    defaultValues: initialFormValues,
    resolver: zodResolver(Schema),
    mode: 'all',
  });

  const updateTaxRateData = useMutation({
    mutationFn: (data: FieldValues) =>
      api.fetch('edit_tax_rate', {
        id: taxData.tax_id,
        name: data.name,
        category: data.category,
        partnerId: partnerId,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [
          'get_default_tax_product_class',
          'tax_rate,product_class',
          1,
          100,
          partnerId,
        ],
      });
      ToastHelper.showToast(
        'Tax Rate',
        ToastType.SUCCESS,
        ToastMessageType.UPDATE,
      );
      handleOpenEditTaxRateModal();
    },
    onError: (err: ApiErrorResponse) => {
      const errorMessages = parseApiResponseError(err?.response?.data?.error);
      if (errorMessages?.length > 0) {
        errorMessages.map((errorMessage) => {
          ToastHelper.showToast(
            'Tax Rate',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            errorMessage,
          );
        });
      } else {
        ToastHelper.showToast(
          'Tax Rate',
          ToastType.ERROR,
          ToastMessageType.ERROR,
        );
      }
    },
  });

  const onSubmit = (data: FieldValues) => {
    updateTaxRateData.mutate(data);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContainerRef.current && event.target instanceof Node) {
        if (
          modalContainerRef.current &&
          !modalContainerRef.current.contains(event.target)
        ) {
          handleOpenEditTaxRateModal();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className='fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-50 flex justify-center items-center z-50'>
      <div
        ref={modalContainerRef}
        className='bg-white p-8 rounded-md w-[742px] h-[366px]'
      >
        {isProductClassesLoading ? (
          <div className='flex justify-center items-center w-full h-full'>
            <Loader fillColor='#6923F4' />
          </div>
        ) : (
          <div className='w-full h-full flex-col'>
            <div className='w-full flex justify-between'>
              <h1>Edit Tax Rate</h1>
              <img
                onClick={() => {
                  handleOpenEditTaxRateModal();
                }}
                src={closeIcon}
                width={10}
                alt='close-icon'
                className='cursor-pointer'
              />
            </div>
            <Divider customStyle='mt-5' />
            <div className='flex-col w-full pt-6 pb-6'>
              <CustomInput
                id='name'
                label='Name'
                {...register('name')}
                error={errors['name']?.message}
              />
              <SelectInput
                label='Category'
                disabled
                options={convertProductClassesToSelectOptions(
                  productClassesDataResponse || { data: [] },
                )}
                value={watch('category')}
                {...register('category')}
              />
            </div>
            <div className='flex justify-between'>
              <Button
                label='Cancel'
                onClick={() => {
                  handleOpenEditTaxRateModal();
                }}
                dataTestId='cancelButton'
                className='w-[87px] h-[40px] rounded-[39px]'
                variant={ButtonVariant.Ghost}
              />
              <Button
                variant={ButtonVariant.Default}
                onClick={handleSubmit(onSubmit)}
                disabled={
                  (!Object.keys(dirtyFields).length &&
                    watch('category') ===
                      convertLabelToSlug(taxData?.product_class_name)) ||
                  !!Object.keys(errors).length
                }
                label={'Save'}
                className='w-[70px] h-[40px] rounded-[39px]'
                loading={updateTaxRateData.isPending}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditTaxRateModal;
